import { Component, inject, OnDestroy } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { BoelsGoogleMapsComponent } from '../../boels-google-maps/boels-google-maps.component';
import { AsyncPipe, JsonPipe, ViewportScroller } from '@angular/common';
import {
  BoelsPointOfService,
  CmsComponentData,
  UspComponentModel,
  UspDirection,
} from '@boels/models';
import {
  catchError,
  map,
  Observable,
  Observer,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { PrepareHtmlPipe, StoreFinderFacade } from '@boels/shared-state';
import { Params, Router } from '@angular/router';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import {
  BreakpointService,
  HelpersModule,
  PhoneFilterPipe,
  QaHooksDirective,
  RoutingService,
} from '@boels/helpers';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UspComponent } from '../../usp/usp.component';
import { WellComponent } from '@boels/elements';
import { VerticalAlignerComponent } from '../../vertical-aligner/vertical-aligner.component';
import { MediaComponent } from '../../media/media.component';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'boels-store-finder-details',
  templateUrl: './store-finder-details.component.html',
  styleUrls: ['./store-finder-details.component.scss'],
  imports: [
    GoogleMapsModule,
    BoelsGoogleMapsComponent,
    JsonPipe,
    AsyncPipe,
    TranslocoModule,
    FontAwesomeModule,
    SpinnerComponent,
    NgbTooltipModule,
    HelpersModule,
    PhoneFilterPipe,
    MediaComponent,
    UspComponent,
    WellComponent,
    PrepareHtmlPipe,
    VerticalAlignerComponent,
    QaHooksDirective,
    ButtonComponent,
  ],
  providers: [
    BreakpointService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['storeFinder'],
    },
  ],
})
export class StoreFinderDetailsComponent implements OnDestroy {
  private storeFinderFacade = inject(StoreFinderFacade);
  private routingService = inject(RoutingService);
  private router = inject(Router);
  private viewPortScroller = inject(ViewportScroller);

  infoIcon = faInfoCircle;
  store$: Observable<BoelsPointOfService | null> = this.routingService
    .getRoutePropertyAsObservable('params')
    .pipe(
      takeUntilDestroyed(),
      map((params: Params | undefined) => params?.['store']),
      switchMap((param: string) =>
        this.storeFinderFacade.getPointOfService(param),
      ),
      tap((storeFinder) => this.storeFinderFacade.setActiveStore(storeFinder)),
      catchError((error) => {
        throwError(() => error);
        // navigate user to branches page
        this.router.navigate(['/branches']);
        return of(null);
      }),
    );

  cartUspNodes$: CmsComponentData<UspComponentModel> = {
    data$: new Observable((observer: Observer<UspComponentModel>) => {
      const uspComponentModel: UspComponentModel = {
        direction: UspDirection.VERTICAL,
        isCartUsp: true,
      };
      observer.next(uspComponentModel);
      observer.complete();
    }),
  };

  #breakPointService = inject(BreakpointService);
  readonly $isMobile = toSignal(this.#breakPointService.mdAndBelow$, {
    initialValue: false,
  });

  ngOnDestroy(): void {
    this.storeFinderFacade.setActiveStore(undefined);
  }

  scrollToAnchor(anchor: string): void {
    this.viewPortScroller.scrollToAnchor(anchor);
  }
}
